import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {
    ReferenceBookResponse, ReferenceBookType, SourceType, TrendApiService, TrendFilterRequest, TrendsResponse, TrendStatus, TrendType,
} from '@npck/af-frontend-api';
import {
    DATE_SERVER_FORMAT,
    DATE_TIME_SERVER_FORMAT,
    DATE_UI_FORMAT, downloadFromBlob,
    LocalizationService,
    RouterService,
    rxSpinner,
    TAB,
} from '@npck/libs/commons';
import {SnackbarService} from '@npck/libs/ui-kit';
import dayjs from 'dayjs';
import {combineLatest, Subject, Subscription} from 'rxjs';
import {
    fieldExtractor,
    FilterDictionaryField,
    FilterField, getFormattedDateOrNull,
} from '../../../components/full-table/filter/filter.component';
import {mergeTableEvent, TableEvent} from '../../../components/full-table/full-table.component';
import {
    Column,
    ColumnType,
    ELLIPSIS_LARGE_TEXT_MAX_LENGTH,
    ELLIPSIS_MEDIUM_TEXT_MAX_LENGTH,
    Row,
    StatusColumn,
} from '../../../components/full-table/table/table.component';
import {mapTrendStatusToStyle} from '../../../components/status/status.component';
import {DictionaryHolderService, findValueInDictionary} from '../../../domain/dictionary-holder.service';
import {TREND_ID} from '../../../domain/query-params.keys';
import {TrendsFilterHolderService} from '../../../domain/trends-filter-holder.service';
import {Path} from '../../../path';
import {ANALYTIC_TREND_TAB, AnalyticsTab} from '../analytics.component';

@Component({
    selector: 'af-analytic-trends',
    templateUrl: 'trends.component.html',
    standalone: false
})
export class TrendsComponent implements OnInit, OnDestroy {
    @Input() buttonClickStream: Subject<AnalyticsTab>;
    Path = Path;
    tableEvent: TableEvent;
    filterFields: FilterField[] = [
        {
            type: 'text',
            name: 'query',
        },
        <FilterDictionaryField>{
            isMultiSelect: true,
            type: 'select',
            name: 'trendStatus',
            dictionaryName: 'trend_status',
            values: [],
        },
        <FilterDictionaryField>{
            isMultiSelect: true,
            type: 'select',
            name: 'trendType',
            dictionaryName: 'trend_type',
            values: [],
        },
        <FilterDictionaryField>{
            isMultiSelect: true,
            type: 'select',
            name: 'country',
            dictionaryName: 'country',
            values: [],
        },
        <FilterDictionaryField>{
            isMultiSelect: true,
            type: 'select',
            name: 'region',
            dictionaryName: 'region',
            values: [],
        },
        <FilterDictionaryField>{
            isMultiSelect: true,
            type: 'select',
            name: 'sourceType',
            dictionaryName: 'source_type',
            values: [],
        },
        <FilterField>{
            type: 'dateRange',
            name: 'trendDetectedDateRange',
        },
        <FilterField>{
            type: 'dateRange',
            name: 'separatedDateRange',
        },
        <FilterField>{
            type: 'dateRange',
            name: 'trendUpdatedDateRange',
        }
    ];
    dataSource: Row[] = [];
    totalElements: number;
    isDestroyed = false;
    organizations: ReferenceBookResponse[];
    buttonClickSubscription: Subscription;
    exportReportSubscription: Subscription;

    constructor(
        public filterHolderService: TrendsFilterHolderService,
        private trendApiService: TrendApiService,
        private routerService: RouterService,
        private dictionaryHolderService: DictionaryHolderService,
        private localizationService: LocalizationService,
        private snackbarService: SnackbarService,
    ) {
    }

    ngOnInit(): void {
        this.buttonClickSubscription = this.buttonClickStream.subscribe(activeTab => this.downloadTrendsReport());
        this.loadTrends(this.filterHolderService.getState().tableEvent);
    }

    ngOnDestroy(): void {
        this.buttonClickSubscription?.unsubscribe();
        this.exportReportSubscription?.unsubscribe();
        this.isDestroyed = true;
    }

    loadTrends(tableEvent: TableEvent): void {
        this.tableEvent = mergeTableEvent(this.filterHolderService.getState().tableEvent, tableEvent);
        this.filterHolderService.storeTableState(this.tableEvent);
        if (this.tableEvent.spinnerId !== 'paging') {
            this.dataSource = [];
            this.totalElements = 0;
        }

        combineLatest([
            this.dictionaryHolderService.getReference(ReferenceBookType.TREND_TYPE),
            this.dictionaryHolderService.getReference(ReferenceBookType.TREND_STATUS),
            this.dictionaryHolderService.getReference(ReferenceBookType.COUNTRY),
            this.dictionaryHolderService.getReference(ReferenceBookType.SOURCE_TYPE),
            this.dictionaryHolderService.getReference(ReferenceBookType.FRAUD_TYPE),
            this.trendApiService.getTrends(this.buildFilterRequest())
        ])
            .pipe(rxSpinner(this.tableEvent.spinnerId))
            .subscribe(([trendTypes, trendStatuses, countries, sourceTypes, fraudTypes, trendsResponse]) => {
                this.filterHolderService.storeResponse(trendsResponse);
                this.buildDataSource(trendTypes, trendStatuses, countries, sourceTypes, fraudTypes, trendsResponse);
            });
    }

    downloadTrendsReport(): void {
        const request = this.trendApiService.exportTrends(this.buildFilterRequest());

        const fileName = this.localizationService.getLocalizedKey('i18n_af_report#trends');
        this.exportReportSubscription = request.subscribe({
            next: file => {
                downloadFromBlob(file, `${fileName}.xlsx`);
                this.snackbarService.showSnackBar('i18n_af_snackBarMessageTrendReportSuccessfullyExported');
            }, error: err => {
                throw err;
            },
        });
    }

    private buildFilterRequest(): TrendFilterRequest {
        return {
            search: this.tableEvent.filter?.query,
            statuses: fieldExtractor(this.tableEvent.filter?.trendStatus) as TrendStatus[],
            types: fieldExtractor(this.tableEvent.filter?.trendType) as TrendType[],
            countries: fieldExtractor(this.tableEvent.filter?.country),
            regions: fieldExtractor(this.tableEvent.filter?.region),
            sources: fieldExtractor(this.tableEvent.filter?.sourceType) as SourceType[],
            detectedDateFrom: getFormattedDateOrNull(
                this.tableEvent.filter?.trendDetectedDateRange?.dateFrom,
                DATE_SERVER_FORMAT,
            ),
            detectedDateTo: getFormattedDateOrNull(
                this.tableEvent.filter?.trendDetectedDateRange?.dateTo,
                DATE_SERVER_FORMAT,
            ),
            createDateFrom: getFormattedDateOrNull(
                this.tableEvent.filter?.separatedDateRange?.startDate?.date,
                DATE_TIME_SERVER_FORMAT,
            ),
            createDateTo: getFormattedDateOrNull(
                this.tableEvent.filter?.separatedDateRange?.endDate?.date,
                DATE_TIME_SERVER_FORMAT,
            ),
            updatedDateFrom: getFormattedDateOrNull(
                this.tableEvent.filter?.trendUpdatedDateRange?.startDate?.date,
                DATE_TIME_SERVER_FORMAT,
            ),
            updatedDateTo: getFormattedDateOrNull(
                this.tableEvent.filter?.trendUpdatedDateRange?.endDate?.date,
                DATE_TIME_SERVER_FORMAT,
            ),
            limit: this.tableEvent.page?.pageSize,
            offset: this.tableEvent.page?.pageOffset,
            sortBy: this.tableEvent.sortBy?.columnName,
            sortOrder: this.tableEvent.sortBy?.direction,
        } as TrendFilterRequest;
    }

    private buildDataSource(
        trendTypes: ReferenceBookResponse[],
        trendStatuses: ReferenceBookResponse[],
        countries: ReferenceBookResponse[],
        sourceTypes: ReferenceBookResponse[],
        fraudTypes: ReferenceBookResponse[],
        response: TrendsResponse
    ): void {
        this.totalElements = response.total;
        this.dataSource = response.items.map(item => <Row>{
            click: () => this.openTrendDetails(item.id),
            createdDate: <Column>{
                type: ColumnType.DATE_TIME_SECONDS,
                sortable: true,
                text: item.createdDate,
            },
            status: <StatusColumn>{
                type: ColumnType.STATUS,
                text: item.isDeleted
                    ? this.localizationService.getLocalizedKey('i18n_af_statusDeleted')
                    : (findValueInDictionary(trendStatuses, item.status, 'name_ru') ?? '-'),
                style: mapTrendStatusToStyle(item.status, item.isDeleted),
            },
            trendType: <Column>{
                type: ColumnType.TEXT,
                text: findValueInDictionary(trendTypes, item.type, 'name_ru') ?? '-',
            },
            country: <Column>{
                type: ColumnType.TEXT,
                text: findValueInDictionary(countries, item.country, 'name_ru') ?? '-',
            },
            region: <Column>{
                type: ColumnType.TEXT,
                text: item.regions?.length && item.regions[0] || '-',
                cellStyle: item.regions?.join('\n')?.length > ELLIPSIS_LARGE_TEXT_MAX_LENGTH && {
                    ellipsisTextLarge: true,
                },
                tooltip: item.regions?.join('\n')?.length > ELLIPSIS_MEDIUM_TEXT_MAX_LENGTH && {
                    text: item.regions?.join('\n'),
                },
            },
            source: <Column>{
                type: ColumnType.TEXT,
                text: findValueInDictionary(sourceTypes, item.source, 'name_ru') ?? '-',
            },
            trendFraudType: <Column>{
                type: ColumnType.TEXT,
                text: findValueInDictionary(fraudTypes, item.fraudType, 'name_ru') ?? '-',
            },
            trendFraudScheme: <Column>{
                type: ColumnType.TEXT,
                text: item.fraudScheme
            },
            periodDetected: <Column>{
                type: ColumnType.TEXT,
                sortable: true,
                text: [item.periodDetectedFrom, item.periodDetectedTo].filter(n => !!n)
                    .map(n => dayjs(n).format(DATE_UI_FORMAT)).join(' - '),
            },
            lastRecalculatedDate: <Column>{
                type: ColumnType.DATE_TIME_SECONDS,
                sortable: true,
                text: item.lastRecalculatedDate,
            },
            id: <Column>{
                type: ColumnType.TEXT,
                text: item.id,
                cellStyle: {
                    ellipsisTextLarge: true,
                },
            },
        });
    }

    private openTrendDetails(trendId: string): void {
        this.routerService.navigate(Path.ANALYTICS_TREND_DETAILS, {
            pathParams: {
                [TREND_ID]: trendId,
            },
            queryParams: {
                [TAB]: ANALYTIC_TREND_TAB,
            }
        });
    }
}
